import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import SharedHeader from '../../components/shared/SharedHeader';
import EmptyState from '../../components/shared/EmptyState';
import Loader from '../../components/shared/Loader';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import Pagination from '../../components/shared/Pagination';
import ProtectedPage from '../../components/shared/ProtectedPage';
import RevenueFilterPopup from './components/RevenueFilterPopup';
import DistributorPickerModal from './components/DistributorPickerModal';
import { toast } from '../../components/shared/toast';
import { getApiUrl } from '../../utils/backendUrl';
import { getAuthHeaders } from '../../utils/authHeaders';
import { buildRevenueSongsExportRows, exportRevenueSongsCsv } from '../../utils/revenueExport';
import RevenueSummaryCards from './components/RevenueSummaryCards';
import RevenueSongTable from './components/RevenueSongTable';
import RevenueSongDetails from './components/RevenueSongDetails';
import RevenueArtistCards from './components/RevenueArtistCards';
import RevenueArtistDetails from './components/RevenueArtistDetails';
import RevenueArtistSongs from './components/RevenueArtistSongs';
import RecodeLabelCard from '../RecodeLabels/components/RecodeLabelCard';
import { apiClient } from '../../api/apiClient';
import { usePermissions } from '../../utils/usePermissions';
import { GET_REVENUE_URL } from '../../api/revenue';
import { GET_RECORD_LABELS_URL, GET_RECORD_LABEL_SONGS_URL } from '../../api/recordLabel';
import './Revenue.css';
import '../Ringtone/Ringtone.css';

function Revenue({ onSelectSong }) {
  const { canCreate, canEdit } = usePermissions();
  const canCreateRevenue = canCreate('revenue');
  const canEditRevenue = canEdit('revenue');

  const [selectedType, setSelectedType] = useState('songs'); // Initially load 'songs' sub UI
  const [selectedSongForRevenue, setSelectedSongForRevenue] = useState(null);
  const [selectedArtistForRevenue, setSelectedArtistForRevenue] = useState(null);
  const [selectedArtistForSongsView, setSelectedArtistForSongsView] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [labelSongs, setLabelSongs] = useState([]);
  const [labelSongsCurrentPage, setLabelSongsCurrentPage] = useState(1);
  const [revenueData, setRevenueData] = useState(null);
  const [recordLabels, setRecordLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [fileToImport, setFileToImport] = useState(null);
  const [showImportConfirmModal, setShowImportConfirmModal] = useState(false);
  const [showDistributorPicker, setShowDistributorPicker] = useState(false);
  // null = no card filter, 'high' = Earn > $2000, 'mid' = $1000–$2000
  const [selectedCardFilter, setSelectedCardFilter] = useState(null);
  
  const fileInputRef = useRef(null);
  const songsPerPage = 20;

  // Fetch revenue data when type or date filters change
  const fetchRevenue = useCallback(async (type, fDate, tDate) => {
    if (!type || type === 'record_labels') {
      setRevenueData(null);
      return;
    }

    try {
      setIsLoading(true);
      let url = `${GET_REVENUE_URL}?type=${type}`;
      if (fDate) url += `&fromDate=${fDate}`;
      if (tDate) url += `&toDate=${tDate}`;

      const res = await apiClient(url);
      setRevenueData(res || null);
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      toast.error('Failed to load revenue data.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch record labels for the record_labels category view
  const fetchRecordLabels = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = `${GET_RECORD_LABELS_URL}?page=1&limit=100&search=${encodeURIComponent(searchQuery.trim())}`;
      const response = await apiClient(url);
      if (response) {
        setRecordLabels(response.labels || response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch record labels:', err);
      toast.error('Failed to load record labels.');
      setRecordLabels([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery]);

  // Fetch songs for a selected record label
  const fetchLabelSongs = useCallback(async (labelId, pageNum = 1) => {
    try {
      setIsLoading(true);
      
      // Fetch record label songs
      const labelSongsUrl = `${GET_RECORD_LABEL_SONGS_URL(labelId)}?page=${pageNum}&limit=100`;
      const labelSongsResponse = await apiClient(labelSongsUrl);
      
      if (labelSongsResponse && labelSongsResponse.songs) {
        const labelSongsList = labelSongsResponse.songs || [];
        const labelSongIds = labelSongsList.map(s => s.id);
        
        // Fetch all revenue songs data
        const revenueSongsUrl = `${GET_REVENUE_URL}?type=songs`;
        const revenueResponse = await apiClient(revenueSongsUrl);
        
        if (revenueResponse && revenueResponse.songs) {
          // Filter revenue songs to only those in the record label
          const revenueSongsMap = {};
          revenueResponse.songs.forEach(song => {
            revenueSongsMap[song.id] = song;
          });
          
          // Merge record label songs with revenue data
          const mergedSongs = labelSongsList.map(labelSong => ({
            ...labelSong,
            ...(revenueSongsMap[labelSong.id] || {}),
            name: labelSong.name || revenueSongsMap[labelSong.id]?.name,
            artist: labelSong.artist || revenueSongsMap[labelSong.id]?.artist,
            ownership: labelSong.ownership || revenueSongsMap[labelSong.id]?.ownership
          }));
          
          setLabelSongs(mergedSongs);
        } else {
          setLabelSongs(labelSongsList);
        }
      }
    } catch (err) {
      console.error('Failed to fetch label songs:', err);
      toast.error('Failed to load label songs.');
      setLabelSongs([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedType === 'record_labels') {
      fetchRecordLabels();
    } else {
      fetchRevenue(selectedType, fromDate, toDate);
    }
  }, [selectedType, fromDate, toDate, fetchRevenue, fetchRecordLabels]);

  const handleSelectLabel = (label) => {
    setSelectedLabel(label);
    setLabelSongsCurrentPage(1);
    fetchLabelSongs(label.id, 1);
  };

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    setSelectedCardFilter(null);
    setSelectedSongForRevenue(null);
    setSelectedArtistForRevenue(null);
    setSelectedArtistForSongsView(null);
    setSelectedLabel(null);
    setSearchQuery('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const handleExportTemplate = async (distributor) => {
    try {
      setIsExporting(true);
      setShowDistributorPicker(false);

      let exportUrl = getApiUrl('/revenue/template-export');
      if (distributor?.id) {
        exportUrl += `?distributorId=${distributor.id}`;
      }

      const response = await fetch(exportUrl, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to generate template export');
      const blob = await response.blob();

      // Derive filename from Content-Disposition header if available
      const disposition = response.headers.get('Content-Disposition') || '';
      const matchFilename = disposition.match(/filename=([^;"\s]+|"[^"]+")/);
      const rawFilename = matchFilename
        ? matchFilename[1].replace(/"/g, '')
        : `revenue_template${distributor ? `_${distributor.company || distributor.email}` : ''}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', rawFilename);
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      const distName = distributor?.company || distributor?.email;
      toast.success(
        distName
          ? `Revenue template for "${distName}" downloaded successfully!`
          : 'Revenue template downloaded successfully!'
      );
    } catch (err) {
      console.error('Template export error:', err);
      toast.error('Failed to export revenue template.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFileToImport(file);
    setShowImportConfirmModal(true);
    if (e.target) e.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!fileToImport) return;
    setShowImportConfirmModal(false);

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('file', fileToImport);

      const res = await apiClient('/revenue/import', {
        method: 'POST',
        body: formData
      });

      toast.success(res.message || 'Revenue data imported successfully!');
      fetchRevenue(selectedType || 'songs', fromDate, toDate);
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.message || 'Failed to import revenue file.');
    } finally {
      setIsLoading(false);
      setFileToImport(null);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const catType = selectedType || 'songs';
      let exportUrl = `${getApiUrl('/revenue/export')}?type=${catType}`;
      if (fromDate) exportUrl += `&fromDate=${fromDate}`;
      if (toDate) exportUrl += `&toDate=${toDate}`;

      const response = await fetch(exportUrl, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to export revenue report');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `revenue_${catType}_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Revenue report exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export revenue report.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSongsExport = () => {
    const rows = buildRevenueSongsExportRows(filteredSongs);
    if (!rows.length) {
      toast.warning('No songs available to export for the current filters.');
      return;
    }

    setIsExporting(true);
    try {
      exportRevenueSongsCsv(rows, `revenue_songs_export_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success('Relevant songs exported successfully!');
    } catch (err) {
      console.error('Songs export error:', err);
      toast.error('Failed to export songs.');
    } finally {
      setIsExporting(false);
    }
  };

  const filteredSongs = useMemo(() => {
    let list = revenueData?.songs || [];

    // Apply card range filter (first two summary cards)
    if (selectedCardFilter === 'high') {
      list = list.filter(s => (s.revenueAmount || 0) > 2000);
    } else if (selectedCardFilter === 'mid') {
      list = list.filter(s => {
        const amt = s.revenueAmount || 0;
        return amt >= 1000 && amt <= 2000;
      });
    }

    // Apply text search
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(s =>
      (s.name && s.name.toLowerCase().includes(q)) ||
      (s.artist && s.artist.toLowerCase().includes(q)) ||
      (s.nameSinhala && s.nameSinhala.toLowerCase().includes(q)) ||
      (s.isrcCode && s.isrcCode.toLowerCase().includes(q))
    );
  }, [revenueData, searchQuery, selectedCardFilter]);

  const filteredCategoryItems = useMemo(() => {
    const list = revenueData?.items || [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(item =>
      (item.name && item.name.toLowerCase().includes(q)) ||
      (item.company_name && item.company_name.toLowerCase().includes(q)) ||
      (item.display_name && item.display_name.toLowerCase().includes(q))
    );
  }, [revenueData, searchQuery]);

  // Paginated items for category sub-tabs (20 per page)
  const paginatedCategoryItems = useMemo(() => {
    const startIndex = (currentPage - 1) * songsPerPage;
    return filteredCategoryItems.slice(startIndex, startIndex + songsPerPage);
  }, [filteredCategoryItems, currentPage]);

  // Paginated songs for the songs sub-tab (20 per page)
  const paginatedSongs = useMemo(() => {
    const startIndex = (currentPage - 1) * songsPerPage;
    return filteredSongs.slice(startIndex, startIndex + songsPerPage);
  }, [filteredSongs, currentPage, songsPerPage]);

  return (
    <ProtectedPage module="revenue">
      <div className="revenue-page-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      
      {/* Top Header Row with Category Selector Dropdown & Action Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Revenue</span>
          {selectedType && (
            <>
              {selectedType === 'artist' && selectedArtistForRevenue ? (
                <>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#64748b' }}>&gt;</span>
                  <button
                    type="button"
                    onClick={() => setSelectedArtistForRevenue(null)}
                    style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#64748b',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#0b66e3'}
                    onMouseLeave={(e) => e.target.style.color = '#64748b'}
                  >
                    Artist
                  </button>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#0b66e3' }}>
                    &gt; {selectedArtistForRevenue.name || 'Artist Name'}
                  </span>
                </>
              ) : (
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#64748b' }}>
                  &gt; {selectedType === 'songs' ? 'Songs' : selectedType === 'artist' || selectedType === 'singer' ? 'Artist' : selectedType === 'record_labels' ? 'Record Labels' : ''}
                </span>
              )}
            </>
          )}
        </div>

        {/* Category Type Dropdown & Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".xlsx, .xls, .csv"
            style={{ display: 'none' }}
          />

          {canEditRevenue && (
            <button
              type="button"
              onClick={() => setShowDistributorPicker(true)}
              disabled={isExporting}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: '#0b66e3',
                color: '#ffffff',
                border: 'none',
                fontWeight: '600',
                fontSize: '13px',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(11,102,227,0.2)'
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span>Template Export</span>
            </button>
          )}

          {canCreateRevenue && (
            <button
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={isLoading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: '#10b981',
                color: '#ffffff',
                border: 'none',
                fontWeight: '600',
                fontSize: '13px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(16,185,129,0.2)'
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Import Excel</span>
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '4px' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Category:</label>
            <select
              className="form-control"
              value={selectedType}
              onChange={handleTypeChange}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                fontWeight: '600',
                color: '#0f172a',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                minWidth: '200px'
              }}
            >
              <option value="songs">Songs</option>
              <option value="artist">Artist</option>
              <option value="record_labels">Record Labels</option>
            </select>
          </div>
        </div>
      </div>

      {/* If No Category Selected -> Show Shared Global No Record Found UI */}
      {!selectedType ? (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '60px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <EmptyState
            title="No Record Found"
            subtitle="Please select a category type (Songs, Artist, or Record Labels) from the dropdown above to view revenue analytics."
            hasSearch={false}
          />
        </div>
      ) : selectedType === 'songs' ? (
        selectedSongForRevenue ? (
          <RevenueSongDetails
            songId={selectedSongForRevenue.id}
            initialSong={selectedSongForRevenue}
            onBack={() => setSelectedSongForRevenue(null)}
          />
        ) : (
          <>
            {/* Summary Metric Cards */}
            <RevenueSummaryCards
              summary={revenueData?.summary || []}
              activeCardId={selectedCardFilter === 'high' ? 1 : selectedCardFilter === 'mid' ? 2 : null}
              onCardClick={(card) => {
                // Toggle: clicking same card again clears the filter
                const nextFilter = card.id === 1 ? 'high' : 'mid';
                setSelectedCardFilter(prev => prev === nextFilter ? null : nextFilter);
                setCurrentPage(1);
              }}
            />

            {/* Shared Control Header with Date Range Filter Popup */}
            <div style={{ position: 'relative' }}>
              <SharedHeader
                title=""
                searchQuery={searchQuery}
                onSearchChange={(val) => { setSearchQuery(val); setCurrentPage(1); }}
                searchPlaceholder="Search"
                resultCount={filteredSongs.length}
                resultLabel="Results"
                onExportClick={handleSongsExport}
                onFilterClick={() => setShowFilterPopup(!showFilterPopup)}
                onAddNewClick={null}
                addNewText="Add New"
                showSortFilter={true}
              />

              {showFilterPopup && (
                <RevenueFilterPopup
                  fromDate={fromDate}
                  toDate={toDate}
                  onFromDateChange={setFromDate}
                  onToDateChange={setToDate}
                  onReset={() => {
                    setFromDate('');
                    setToDate('');
                  }}
                  onClose={() => setShowFilterPopup(false)}
                />
              )}
            </div>

            {/* Revenue Songs Table */}
            <RevenueSongTable
              songs={paginatedSongs}
              currentPage={currentPage}
              totalSongs={filteredSongs.length}
              songsPerPage={songsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
              onSelectSong={(song) => setSelectedSongForRevenue(song)}
              isLoading={isLoading}
            />
          </>
        )
      ) : selectedType === 'artist' ? (
        /* Artist Tab: Card grid UI + Artist Revenue Details + Artist Songs View */
        selectedArtistForSongsView ? (
          <RevenueArtistSongs
            artistId={selectedArtistForSongsView.id}
            artistName={selectedArtistForSongsView.name}
            onBack={() => setSelectedArtistForSongsView(null)}
          />
        ) : selectedArtistForRevenue ? (
          <RevenueArtistDetails
            artistId={selectedArtistForRevenue.id}
            initialArtist={selectedArtistForRevenue}
            onBack={() => setSelectedArtistForRevenue(null)}
            onViewSongs={() => setSelectedArtistForSongsView(selectedArtistForRevenue)}
          />
        ) : (
          <>
            <div style={{ position: 'relative' }}>
              <SharedHeader
                title=""
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search artists..."
                resultCount={(revenueData?.items || []).length}
                resultLabel="Artists"
                onExportClick={handleExport}
                showSortFilter={false}
              />
            </div>
            <RevenueArtistCards
              items={revenueData?.items || []}
              isLoading={isLoading}
              onSelectArtist={(artist) => setSelectedArtistForRevenue(artist)}
              searchQuery={searchQuery}
            />
          </>
        )
      ) : selectedType === 'record_labels' ? (
        /* Record Labels Tab: Card grid UI */
        selectedLabel ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Back Button and Label Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <button
                type="button"
                onClick={() => {
                  setSelectedLabel(null);
                  setLabelSongs([]);
                  setLabelSongsCurrentPage(1);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0f172a',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
              >
                ← Back
              </button>
              <span style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                {selectedLabel.name} - Songs
              </span>
            </div>

            {/* SharedHeader for searching songs */}
            <SharedHeader
              title=""
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search songs..."
              resultCount={labelSongs.length}
              resultLabel="Songs"
              onExportClick={null}
              showSortFilter={false}
            />

            {/* Revenue Song Table */}
            {labelSongs.length === 0 ? (
              <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '40px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <EmptyState
                  title="No Songs Found"
                  subtitle={`No songs are currently associated with ${selectedLabel.name}.`}
                  hasSearch={false}
                />
              </div>
            ) : (
              <RevenueSongTable
                songs={labelSongs}
                currentPage={labelSongsCurrentPage}
                totalSongs={labelSongs.length}
                songsPerPage={songsPerPage}
                onPageChange={setLabelSongsCurrentPage}
                onSelectSong={(song) => setSelectedSongForRevenue(song)}
                isLoading={isLoading}
              />
            )}
          </div>
        ) : (
          <>
            <SharedHeader
              title=""
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search record labels..."
              resultCount={recordLabels.length}
              resultLabel="Labels"
              onExportClick={null}
              showSortFilter={false}
            />

            {/* Grid of Record Label Cards */}
            <div className="ringtone-grid-container">
              {isLoading ? (
                <Loader label="Fetching record labels, please wait." />
              ) : recordLabels.length > 0 ? (
                recordLabels.map((label) => (
                  <RecodeLabelCard 
                    key={label.id}
                    label={label}
                    onSelect={handleSelectLabel}
                    onEdit={null}
                    onInactivate={null}
                    onActivate={null}
                    onDelete={null}
                    t={(key) => key}
                  />
                ))
              ) : (
                <EmptyState title="No Record Labels Found" subtitle="No record labels match your search criteria." hasSearch={true} />
              )}
            </div>
          </>
        )
      ) : (
        /* Fallback for other category types */
        <></>
      )}

      {/* Distributor Picker Modal — for Template Export */}
      <DistributorPickerModal
        isOpen={showDistributorPicker}
        onClose={() => setShowDistributorPicker(false)}
        onConfirm={(dist) => handleExportTemplate(dist)}
      />

      {/* Primary Import Confirmation Modal */}
      <ConfirmationModal
        isOpen={showImportConfirmModal}
        type="info"
        title="Confirm Revenue Excel Import"
        message={`Are you sure you want to import the revenue file "${fileToImport?.name}" into the database?`}
        confirmLabel="Import Data"
        cancelLabel="Cancel"
        onConfirm={handleConfirmImport}
        onCancel={() => {
          setShowImportConfirmModal(false);
          setFileToImport(null);
        }}
      />

      {/* Global Loading Overlay */}
      <LoadingOverlay isLoading={isLoading || isExporting} message={isExporting ? "Exporting revenue report..." : "Loading revenue..."} />
    </div>
    </ProtectedPage>
  );
}

export default Revenue;
